import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import popups from 'src/lib/ui/config/popups';
import { Currencies } from '../../replicant/buruburu/inventory/inventory.ruleset';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';

type CloseHandler = () => void;
type Purchase = { old: Currencies; new: Currencies };

/**
 * Purchase complete confirmation popup
 */
export default class PopupPurchaseComplete extends PopupBasic {
  private ok: ButtonScaleViewWithText;
  private close: CloseHandler;
  private received: (purchase: Purchase) => void;

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 410,
      darkerBg: true,
      tracking: {
        feature: 'gems-shop',
        $subFeature: 'gems-exchanged-popup',
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.ok = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 338,
      width: 228,
      height: 95,
      centerOnOrigin: true,
      localeText: () => i18n('basic.ok'),
      tracking: { feature: 'gems-shop', $subFeature: 'gems-exchanged-ok' },
    });

    this.message.updateOpts({
      x: this.box.style.width / 2,
      y: 107,
      width: 418,
      height: 54,
      size: 40,
      color: '#BF8147',
      centerOnOrigin: true,
    });

    this.received = this.exchangedView();
  }

  /**
   * On dialog init
   */
  init(opts: Purchase) {
    super.init({
      title: i18n('currencyExchange.purchaseComplete.title'),
      message: i18n('currencyExchange.purchaseComplete.message'),
    });

    // Update UI
    this.received(opts);

    // Enable from previous dialog show
    this.ok.setDisabled(false);

    // Create on OK click event
    this.ok.onClick = async () => {
      this.ok.setDisabled(true);
      sounds.sfx.play('ok');
      this.close();
    };
  }

  /**
   * Exchanged view
   */
  private exchangedView() {
    new ImageScaleView({
      tag: 'underline',
      superview: this.box,
      ...popups.underline,
      width: 426,
      height: 4,
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: 134,
    });

    const purchaseResult = this.box.addSubview(
      new FlexBoxView({
        x: 20,
        y: 165,
        width: this.box.style.width - 40,
        flex: {
          alignItems: 'center',
        },
      }),
    );

    const priceFrame = purchaseResult.addSubview(
      new ImageScaleView({
        image: 'assets/ui/shop/frames/panel_unit.png',
        scaleMethod: '9slice',
        width: 250,
        height: 102,
        sourceSlices: {
          horizontal: { left: 24, right: 24 },
          vertical: { top: 24, bottom: 24 },
        },
      }),
    );

    const priceShadow = new LangBitmapFontTextView({
      tag: 'priceShadow',
      superview: priceFrame,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#000000',
      opacity: 0.25,
      wordWrap: false,
      x: 109,
      y: 24,
      size: 49,
      width: 112,
      height: 59,
      font: bitmapFonts('Body'),
      letterSpacing: -2,
    });

    const price = new LangBitmapFontTextView({
      tag: 'price',
      superview: priceFrame,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#F1F5EF',
      wordWrap: false,
      x: 109,
      y: 24 - 4,
      size: 49,
      width: 112,
      height: 59,
      font: bitmapFonts('Body'),
      letterSpacing: -2,
    });

    priceFrame.addSubview(
      new ImageView({
        tag: 'gem',
        image: 'assets/minigame/match3/gem/gameboard_gem_tile.png',
        width: 80,
        height: 80,
        x: 15,
        y: 12,
      }),
    );

    getFlexNode(
      purchaseResult.addSubview(
        new ImageView({
          image: 'assets/ui/tutorial/tutorial_arrow_active.png',
          width: 74,
          height: 68,
        }),
      ),
    ).updateStyle({
      margin: 10,
    });

    const afterPriceFrame = purchaseResult.addSubview(
      new ImageScaleView({
        image: 'assets/ui/shop/frames/panel_unit.png',
        scaleMethod: '9slice',
        width: 250,
        height: 102,
        sourceSlices: {
          horizontal: { left: 24, right: 24 },
          vertical: { top: 24, bottom: 24 },
        },
      }),
    );

    const afterPriceShadow = new LangBitmapFontTextView({
      superview: afterPriceFrame,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#000000',
      opacity: 0.25,
      wordWrap: false,
      x: 109,
      y: 24,
      size: 49,
      width: 112,
      height: 59,
      font: bitmapFonts('Body'),
      letterSpacing: -2,
    });

    const afterPrice = new LangBitmapFontTextView({
      superview: afterPriceFrame,
      centerAnchor: true,
      align: 'left',
      verticalAlign: 'center',
      color: '#F1F5EF',
      wordWrap: false,
      x: 109,
      y: 24 - 4,
      size: 49,
      width: 112,
      height: 59,
      font: bitmapFonts('Body'),
      letterSpacing: -2,
    });

    afterPriceFrame.addSubview(
      new ImageView({
        tag: 'gem',
        image: 'assets/minigame/match3/gem/gameboard_gem_tile.png',
        width: 80,
        height: 80,
        x: 15,
        y: 12,
      }),
    );

    return ({ old, new: newCurrencies }: Purchase) => {
      const oldGems = old.paidGems + old.freeGems;
      priceShadow.localeText = () => `${parseAmount(oldGems)}`;
      price.localeText = () => `${parseAmount(oldGems)}`;

      const newGems = newCurrencies.paidGems + newCurrencies.freeGems;
      afterPriceShadow.localeText = () => `${parseAmount(newGems)}`;
      afterPrice.localeText = () => `${parseAmount(newGems)}`;
    };
  }
}
